import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ButtonRow } from '@components/styles';
import useNavigation from '@hooks/useNavigation';
import { EntityTypeEnum, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import ExpeditionPrefillCheckboxes from '@components/RingilComponents/ExpeditionPrefillCheckboxes.tsx';
import {
  PrefillOptions,
  useExpeditionPrefillDefaults,
} from '@components/RingilComponents/expeditionPrefillOptions.ts';

interface ComponentProps {
  visible: boolean;
  onCancel: () => void;
  isLinkedToSN: boolean;
  applicationId: string;
  expeditionId: string;
}

const initialPrefillOptions: PrefillOptions = {
  routeAndParameters: true,
  cargoAndOrders: true,
  labels: true,
  internalInfo: true,
  documents: true,
  carrierSelection: true,
  carrierNote: true,
  linkToSN: false,
  copySNAttachments: false,
  markAsPrivate: false,
};

const DuplicateExpeditionDialog: React.FC<ComponentProps> = ({ visible, onCancel, isLinkedToSN, expeditionId }) => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const { hasPermission } = useContext(AuthenticatedUserContext);
  const editDistributionAllowed = hasPermission([
    PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW,
    PermissionTypesEnum.PERMISSION_CARRIER_GROUP_RW,
    PermissionTypesEnum.PERMISSION_CARRIER_GROUP_READ,
  ]);

  const [prefillOptions, setPrefillOptions] = useState<PrefillOptions>(initialPrefillOptions);
  const settingsDefaults = useExpeditionPrefillDefaults();
  useEffect(() => {
    if (settingsDefaults) setPrefillOptions(settingsDefaults);
  }, [settingsDefaults]);

  const update = (patch: Partial<PrefillOptions>) => setPrefillOptions(prev => ({ ...prev, ...patch }));

  const save = () => {
    const queryParams = new URLSearchParams({
      routeAndParameters: prefillOptions.routeAndParameters.toString(),
      cargoAndOrders: prefillOptions.cargoAndOrders.toString(),
      documents: prefillOptions.documents.toString(),
      labels: prefillOptions.labels.toString(),
      internalInfo: prefillOptions.internalInfo.toString(),
      carrierSelection: prefillOptions.carrierSelection.toString(),
      carrierNote: prefillOptions.carrierNote.toString(),
      linkToSN: (isLinkedToSN && !!prefillOptions.linkToSN).toString(),
      copySNAttachments: (isLinkedToSN && !!prefillOptions.copySNAttachments).toString(),
      markAsPrivate: (isLinkedToSN && !!prefillOptions.markAsPrivate).toString(),
    });

    navigation.navigate(
      navigation.urlFunctions.createExpeditionFromEntity(expeditionId, EntityTypeEnum.EXPEDITION, { queryParams: queryParams })
    );
  };

  return (
    <>
      {/*
        // @ts-ignore*/}
      <Dialog
        visible={visible}
        baseZIndex={1000000}
        onHide={() => onCancel()}
        header={<h4>{tr(`DuplicateExpeditionDialog.duplicateExpedition`, `Duplicate expedition`)}</h4>}
        className="dialog-confirm dialog-md"
        style={{ width: '500px' }}
      >
        <p>{tr(`DuplicateExpeditionDialog.title`, `Co chcete pri duplikaci zachovat? Vazba na Expedici bude zachovana.`)}</p>

        <ExpeditionPrefillCheckboxes
          value={prefillOptions}
          onChange={update}
          editDistributionAllowed={editDistributionAllowed}
          isSourceLinkedToSn={isLinkedToSN}
        />

        <ButtonRow>
          <Button label={tr(`DuplicateExpeditionDialog.duplicate`, `Duplicate`)} onClick={() => save()} />
          <Button label={tr(`DuplicateExpeditionDialog.cancel`, `Cancel`)} className="p-button-text" onClick={() => onCancel()} />
        </ButtonRow>
      </Dialog>
    </>
  );
};

export default DuplicateExpeditionDialog;
